import type { User } from "firebase/auth";
import {
  collection,
  doc,
  documentId,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  type DocumentData,
  type Query,
  type Timestamp,
  type Unsubscribe
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import type { Company, ExternalServiceRules } from "../types/imobi";
import { db, storage } from "./firebase";

export type CompanyOperationalSettings = {
  allowResponsibleSelfTransfer: boolean;
  defaultLabelTemplateId: string;
  defaultResponsibleMode: "company" | "required";
  activePlanId?: string;
  activePlanName?: string;
  maxAssets?: number;
  maxUsers?: number;
  commercialAdjustmentType?: "none" | "discount" | "increase";
  commercialAdjustmentValue?: number;
  trialUntil?: string;
  planExpiresAt?: string;
  commercialNotes?: string;
  externalServicesEnabled: boolean;
  externalServiceUserCanRequestAssets: boolean;
  externalServicesLimitPerMonth?: number;
  externalServiceAssetsLimit?: number;
  trainingEnabled: boolean;
  externalServiceRules?: Partial<ExternalServiceRules>;
  features?: {
    externalServices?: boolean;
  };
};

export const defaultCompanyOperationalSettings: CompanyOperationalSettings = {
  allowResponsibleSelfTransfer: false,
  defaultLabelTemplateId: "",
  defaultResponsibleMode: "company",
  activePlanId: "",
  activePlanName: "",
  maxAssets: 0,
  maxUsers: 0,
  commercialAdjustmentType: "none",
  commercialAdjustmentValue: 0,
  trialUntil: "",
  planExpiresAt: "",
  commercialNotes: "",
  externalServicesEnabled: false,
  externalServiceUserCanRequestAssets: false,
  externalServicesLimitPerMonth: 0,
  externalServiceAssetsLimit: 0,
  trainingEnabled: false,
  features: {
    externalServices: false
  }
};

export type CompanyRecord = Company & {
  cnpjRaw?: string;
  responsibleName?: string;
  responsibleContact?: string;
  logoPath?: string;
  settings?: CompanyOperationalSettings;
  createdByName?: string;
  updatedByName?: string;
};

export type SaveCompanyInput = {
  id?: string;
  cnpj: string;
  cnpjRaw: string;
  legalName: string;
  tradeName: string;
  city: string;
  state: string;
  zipCode: string;
  phone?: string;
  address: string;
  responsibleName?: string;
  responsibleContact?: string;
  status: Company["status"];
  isAuditorCompany?: boolean;
  logoFile?: File | null;
};

function serializeTimestamp(value: unknown): string {
  if (value && typeof value === "object" && "toDate" in value) {
    return (value as Timestamp).toDate().toISOString();
  }

  return typeof value === "string" ? value : "";
}

function mapCompany(id: string, data: Record<string, unknown>): CompanyRecord {
  const settings = data.settings && typeof data.settings === "object"
    ? data.settings as Partial<CompanyOperationalSettings>
    : {};

  return {
    id,
    legalName: String(data.legalName ?? ""),
    tradeName: String(data.tradeName ?? ""),
    cnpj: String(data.cnpj ?? ""),
    cnpjRaw: String(data.cnpjRaw ?? ""),
    address: String(data.address ?? ""),
    city: String(data.city ?? ""),
    state: String(data.state ?? ""),
    zipCode: String(data.zipCode ?? ""),
    phone: data.phone ? String(data.phone) : undefined,
    email: data.email ? String(data.email) : undefined,
    logoUrl: data.logoUrl ? String(data.logoUrl) : undefined,
    logoPath: data.logoPath ? String(data.logoPath) : undefined,
    status: (data.status ?? "ativa") as Company["status"],
    isAuditorCompany: data.isAuditorCompany === true,
    responsibleName: data.responsibleName ? String(data.responsibleName) : undefined,
    responsibleContact: data.responsibleContact ? String(data.responsibleContact) : undefined,
    settings: {
      allowResponsibleSelfTransfer: typeof settings.allowResponsibleSelfTransfer === "boolean" ? settings.allowResponsibleSelfTransfer : defaultCompanyOperationalSettings.allowResponsibleSelfTransfer,
      defaultLabelTemplateId: settings.defaultLabelTemplateId ? String(settings.defaultLabelTemplateId) : defaultCompanyOperationalSettings.defaultLabelTemplateId,
      defaultResponsibleMode: settings.defaultResponsibleMode === "required" ? "required" : "company",
      activePlanId: settings.activePlanId ? String(settings.activePlanId) : "",
      activePlanName: settings.activePlanName ? String(settings.activePlanName) : "",
      maxAssets: typeof settings.maxAssets === "number" ? settings.maxAssets : 0,
      maxUsers: typeof settings.maxUsers === "number" ? settings.maxUsers : 0,
      commercialAdjustmentType: settings.commercialAdjustmentType === "discount" || settings.commercialAdjustmentType === "increase" ? settings.commercialAdjustmentType : "none",
      commercialAdjustmentValue: typeof settings.commercialAdjustmentValue === "number" ? settings.commercialAdjustmentValue : 0,
      trialUntil: settings.trialUntil ? String(settings.trialUntil) : "",
      planExpiresAt: serializeTimestamp(settings.planExpiresAt),
      commercialNotes: settings.commercialNotes ? String(settings.commercialNotes) : "",
      externalServicesEnabled: typeof settings.externalServicesEnabled === "boolean" ? settings.externalServicesEnabled : defaultCompanyOperationalSettings.externalServicesEnabled,
      externalServiceUserCanRequestAssets: typeof settings.externalServiceUserCanRequestAssets === "boolean" ? settings.externalServiceUserCanRequestAssets : defaultCompanyOperationalSettings.externalServiceUserCanRequestAssets,
      externalServicesLimitPerMonth: typeof settings.externalServicesLimitPerMonth === "number" ? settings.externalServicesLimitPerMonth : 0,
      externalServiceAssetsLimit: typeof settings.externalServiceAssetsLimit === "number" ? settings.externalServiceAssetsLimit : 0,
      trainingEnabled: typeof settings.trainingEnabled === "boolean" ? settings.trainingEnabled : defaultCompanyOperationalSettings.trainingEnabled,
      externalServiceRules: settings.externalServiceRules && typeof settings.externalServiceRules === "object" ? settings.externalServiceRules : undefined,
      features: {
        externalServices: settings.features?.externalServices === true
      }
    },
    internalNotes: data.internalNotes ? String(data.internalNotes) : undefined,
    createdAt: serializeTimestamp(data.createdAt),
    createdBy: String(data.createdBy ?? ""),
    createdByName: data.createdByName ? String(data.createdByName) : undefined,
    updatedAt: serializeTimestamp(data.updatedAt),
    updatedBy: data.updatedBy ? String(data.updatedBy) : undefined,
    updatedByName: data.updatedByName ? String(data.updatedByName) : undefined
  };
}

function chunk<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = [];

  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }

  return chunks;
}

function subscribeCompanyQueries(companyQueries: Query<DocumentData>[], onChange: (companies: CompanyRecord[]) => void, onError: (message: string) => void): Unsubscribe {
  const buckets = new Map<number, CompanyRecord[]>();
  const unsubscribes = companyQueries.map((companyQuery, index) =>
    onSnapshot(
      companyQuery,
      (snapshot) => {
        buckets.set(index, snapshot.docs.map((companyDoc) => mapCompany(companyDoc.id, companyDoc.data())));
        onChange(Array.from(buckets.values()).flat().sort((left, right) => left.tradeName.localeCompare(right.tradeName)));
      },
      (error) => onError(error.message)
    )
  );

  return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
}

export function subscribeCompanies(
  scope: { globalRole: "MASTER" | "ADMIN" | "USER"; companyIds: string[] },
  onChange: (companies: CompanyRecord[]) => void,
  onError: (message: string) => void
): Unsubscribe {
  if (!db) {
    onError("Firebase nao configurado.");
    return () => undefined;
  }

  if (scope.globalRole === "MASTER" || scope.globalRole === "ADMIN") {
    const companiesQuery = query(collection(db, "companies"), orderBy("tradeName"));
    return onSnapshot(
      companiesQuery,
      (snapshot) => {
        onChange(snapshot.docs.map((companyDoc) => mapCompany(companyDoc.id, companyDoc.data())));
      },
      (error) => onError(error.message)
    );
  }

  const companyIds = Array.from(new Set(scope.companyIds)).filter(Boolean);
  if (!companyIds.length) {
    onChange([]);
    return () => undefined;
  }

  return subscribeCompanyQueries(
    chunk(companyIds, 10).map((ids) => query(collection(db!, "companies"), where(documentId(), "in", ids))),
    onChange,
    onError
  );
}

export async function companyCnpjExists(cnpjRaw: string, ignoreCompanyId?: string): Promise<boolean> {
  if (!db) {
    return false;
  }

  const companiesQuery = query(collection(db, "companies"), where("cnpjRaw", "==", cnpjRaw), limit(1));
  const snapshot = await getDocs(companiesQuery);
  return snapshot.docs.some((companyDoc) => companyDoc.id !== ignoreCompanyId);
}

export async function saveCompany(input: SaveCompanyInput, user: User): Promise<string> {
  if (!db) {
    throw new Error("Firestore nao configurado.");
  }

  const companyId = input.id ?? `empresa-${input.cnpjRaw}`;
  let logoUrl: string | undefined;
  let logoPath: string | undefined;

  if (input.logoFile) {
    if (!storage) {
      throw new Error("Storage nao configurado.");
    }

    const extension = input.logoFile.name.split(".").pop() ?? "png";
    logoPath = `companies/${companyId}/logos/logo-${Date.now()}.${extension}`;
    const logoRef = ref(storage, logoPath);
    await uploadBytes(logoRef, input.logoFile);
    logoUrl = await getDownloadURL(logoRef);
  }

  const userLabel = user.displayName ?? user.email ?? user.uid;
  const companyRef = doc(db, "companies", companyId);
  const payload = {
    cnpj: input.cnpj,
    cnpjRaw: input.cnpjRaw,
    legalName: input.legalName,
    tradeName: input.tradeName,
    city: input.city,
    state: input.state,
    zipCode: input.zipCode,
    phone: input.phone ?? "",
    address: input.address,
    responsibleName: input.responsibleName ?? "",
    responsibleContact: input.responsibleContact ?? "",
    status: input.status,
    isAuditorCompany: input.isAuditorCompany === true,
    ...(logoUrl ? { logoUrl, logoPath } : {}),
    updatedAt: serverTimestamp(),
    updatedBy: user.uid,
    updatedByName: userLabel,
    ...(input.id ? {} : {
      createdAt: serverTimestamp(),
      createdBy: user.uid,
      createdByName: userLabel
    })
  };

  await setDoc(companyRef, payload, { merge: true });
  return companyId;
}

export async function saveExternalServiceRules(companyId: string, rules: ExternalServiceRules, user: User): Promise<void> {
  if (!db) {
    throw new Error("Firestore nao configurado.");
  }

  // updateDoc com caminho pontilhado grava so as regras, sem sobrescrever o restante de settings
  await updateDoc(doc(db, "companies", companyId), {
    "settings.externalServiceRules": rules,
    updatedAt: serverTimestamp(),
    updatedBy: user.uid,
    updatedByName: user.displayName ?? user.email ?? user.uid
  });
}

export async function saveCompanyOperationalSettings(companyId: string, settings: CompanyOperationalSettings, user: User): Promise<void> {
  if (!db) {
    throw new Error("Firestore nao configurado.");
  }

  const planExpiresAt = settings.trialUntil ? new Date(`${settings.trialUntil}T23:59:59`) : null;

  await setDoc(doc(db, "companies", companyId), {
    settings: {
      ...settings,
      planExpiresAt
    },
    updatedAt: serverTimestamp(),
    updatedBy: user.uid,
    updatedByName: user.displayName ?? user.email ?? user.uid
  }, { merge: true });
}
